// src/components/EditableField.tsx

import React, { useState, useRef, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import { CiEdit } from 'react-icons/ci';

const FiXComponent = FiX as any;
const FiEdit2Component = CiEdit as any;

interface EditableFieldProps {
  value: string;
  onSave: (newValue: string) => Promise<void>;
  fieldName: string;
  className?: string;
  textClassName?: string;
  inputClassName?: string;
  placeholder?: string;
  maxLength?: number;
  type?: 'text' | 'textarea';
  fromTab: string | undefined;
  template?: any;
  existingNames?: string[];
  currentId?: string;
}

const EditableField: React.FC<EditableFieldProps> = ({
  value,
  onSave,
  fieldName,
  className = '',
  textClassName = '',
  inputClassName = '',
  placeholder = '',
  maxLength = 500,
  type = 'text',
  fromTab,
  template,
  existingNames = [],
  currentId,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing) {
      if (type === 'text' && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      } else if (type === 'textarea' && textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.select();
      }
    }
  }, [isEditing, type]);

  useEffect(() => {
    if (!isEditing) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        // Only cancel if there's no error, or if there's an error but user clicked outside
        // We want to keep the input open if there's an error
        if (!error) {
          handleCancel();
        } else {
          // If there's an error, just close without saving
          handleCancel();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditing, value, error]);

  // Validate name for duplicates - called on every keystroke
  const validateName = (name: string): boolean => {
    const trimmed = name.trim();

    // Only validate for 'name' field
    if (fieldName !== 'name') {
      setError('');
      return true;
    }

    if (!trimmed) {
      setError('Name is required');
      return false;
    }

    // Check against existing names (from Redux store)
    if (existingNames.length > 0) {
      // Check if the name already exists (excluding current template's own name)
      const isDuplicate = existingNames.some(
        (existingName) => {
          // Case insensitive comparison
          const isSameName = existingName.toLowerCase() === trimmed.toLowerCase();
          // If currentId exists, exclude the current template's own name
          if (currentId && template?.id === currentId) {
            return isSameName && existingName !== value; // Exclude current name
          }
          return isSameName;
        }
      );

      if (isDuplicate) {
        setError(`A template with the name "${trimmed}" already exists. Please choose a different name.`);
        return false;
      }
    }

    setError('');
    return true;
  };

  const handleEditClick = () => {
    setError('');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditValue(value);
    setError('');
    setIsEditing(false);
  };

  const handleSave = async () => {
    const trimmedValue = editValue.trim();

    // If value hasn't changed, just close editing
    if (trimmedValue === value.trim()) {
      handleCancel();
      return;
    }

    // Validate name for duplicates
    if (!validateName(trimmedValue)) {
      // Keep editing mode open to show error
      return;
    }

    setIsSaving(true);
    try {
      await onSave(trimmedValue);
      setError('');
      setIsEditing(false);
    } catch (error) {
      setEditValue(value);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && type === 'text') {
      e.preventDefault();
      // Only save if there's no error
      if (!error && editValue.trim()) {
        handleSave();
      }
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setEditValue(newValue);

    // Validate on every keystroke
    validateName(newValue);
  };

  // Truncate only name field if > 25 chars
  const isNameField = fieldName === 'name';
  const shouldTruncate = isNameField && value.length > 25;
  const displayValue = shouldTruncate ? `${value.substring(0, 25)}...` : value;
  const showPlaceholder = !value && (placeholder || 'Click to add...');

  return (
    <div
      ref={containerRef}
      className={`relative ${className} ${template && 'flex justify-between'}`}
    >
      {isEditing ? (
        <div className='flex flex-col w-full'>
          <div className='flex items-start gap-2'>
            {type === 'text' ? (
              <input
                ref={inputRef}
                type='text'
                value={editValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={isSaving}
                maxLength={maxLength}
                placeholder={placeholder}
                className={`
                  flex-1 min-w-0 max-w-md px-3 py-1.5 border rounded-md 
                  focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 
                  bg-white text-base ${error ? 'border-red-500' : 'border-teal-300'} ${inputClassName}`}
              />
            ) : (
              <textarea
                ref={textareaRef}
                value={editValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={isSaving}
                maxLength={maxLength}
                placeholder={placeholder}
                rows={3}
                className={`
                  flex-1 min-w-0 max-w-2xl px-3 py-2 border rounded-md 
                  focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 
                  bg-white resize-y ${error ? 'border-red-500' : 'border-teal-300'} ${inputClassName}`}
              />
            )}

            <div className='flex gap-1'>
              <button
                onClick={handleSave}
                disabled={isSaving || !!error || !editValue.trim()}
                className={`p-1.5 rounded disabled:opacity-50 transition ${error || !editValue.trim()
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-teal-600 hover:bg-teal-50'
                  }`}
                title={error || !editValue.trim() ? 'Cannot save - fix errors first' : 'Save'}
              >
                <svg
                  className={`w-4 h-4 ${isSaving ? 'animate-spin' : ''}`}
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  {isSaving ? (
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
                    />
                  ) : (
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M5 13l4 4L19 7'
                    />
                  )}
                </svg>
              </button>

              {fromTab && fromTab === 'yourTemplates' && (
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  className='p-1.5 text-gray-500 hover:bg-gray-100 rounded transition'
                  title='Cancel'
                >
                  <FiXComponent size={16} />
                </button>
              )}
            </div>
          </div>
          {error && (
            <p className='mt-1 text-sm text-red-600 flex items-start gap-1'>
              <span className='inline-block mt-0.5'>⚠️</span>
              <span>{error}</span>
            </p>
          )}
        </div>
      ) : (
        <div className='flex items-center gap-1 w-fit'>
          <div className={`flex-1 min-w-0 ${textClassName}`}>
            {type === 'textarea' && value ? (
              <div className='whitespace-pre-line'>{value}</div>
            ) : (
              <span className={value ? '' : 'text-gray-400'}>
                {showPlaceholder ? (
                  showPlaceholder
                ) : isNameField ? (
                  <div className='group relative inline-block'>
                    <span className='block truncate max-w-2xl'>
                      {displayValue}
                    </span>
                    {shouldTruncate && (
                      <div className='absolute left-0 -top-10 px-3 py-2 bg-gray-800 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-10 shadow-lg'>
                        {value}
                        <svg
                          className='absolute top-full left-4 w-3 h-3 fill-gray-800'
                          viewBox='0 0 10 10'
                        >
                          <polygon points='0,0 10,0 5,5' />
                        </svg>
                      </div>
                    )}
                  </div>
                ) : (
                  value
                )}
              </span>
            )}
          </div>

          {fromTab && fromTab === 'yourTemplates' && (
            <button
              onClick={handleEditClick}
              className='p-1.5 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded transition'
              title={`Edit ${fieldName}`}
            >
              <FiEdit2Component size={20} color={'black'} />
            </button>
          )}
        </div>
      )}
      {template && (
        <div className='flex flex-wrap gap-3'>
          <span className='px-3 py-1 bg-[#eafef5] text-[#4ca786] rounded-full text-xs font-medium flex items-center'>
            {fromTab && fromTab === 'yourTemplates'
              ? 'Your Template'
              : 'Prebuilt Template'}
          </span>
          {
            template?.category && <span className='px-3 py-1 bg-[#eafef5] text-[#4ca786] rounded-full text-xs font-medium flex items-center'>
              {template?.category}
            </span>
          }

          {fromTab && fromTab === 'yourTemplates' && (
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium flex items-center bg-[#eafef5] text-[#4ca786]`}
            >
              {template?.status}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default EditableField;